// app/frontend/components/ui/ContentActionsMenu/ContentActionsMenu.tsx
import { ActionIcon, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { usePage } from "@inertiajs/react";
import {
  IconDots,
  IconEdit,
  IconLink,
  IconCheck,
} from "@tabler/icons-react";

interface CurrentUser {
  admin: boolean;
}

interface PageProps {
  current_user: CurrentUser | null;
}

export interface ContentActionsMenuProps {
  /** URL admins should be sent to when picking "Edit". Omit to hide the item. */
  editUrl?: string;
  /** URL to copy to clipboard. Defaults to the current page URL. */
  shareUrl?: string;
  /** Label for the copy action. */
  copyLabel?: string;
  /** aria-label for the trigger. */
  ariaLabel?: string;
  /** Whether to include the "Copy link" item. Default true. Callers with a
   * dedicated Share button (Posts/Show) pass false to avoid duplicating it. */
  showCopy?: boolean;
}

export function ContentActionsMenu({
  editUrl,
  shareUrl,
  copyLabel = "Copy link",
  ariaLabel = "More actions",
  showCopy = true,
}: ContentActionsMenuProps) {
  const { current_user } = usePage<PageProps>().props;
  const isAdmin = !!current_user?.admin;

  // Nothing to show → render nothing. Avoids an empty kebab button for
  // non-admins when the caller has moved share/copy into a dedicated affordance.
  const showEdit = isAdmin && !!editUrl;
  if (!showCopy && !showEdit) return null;

  const handleCopy = async () => {
    const url = shareUrl ?? window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      notifications.show({
        title: "Link copied",
        message: "Share it wherever you like.",
        color: "orange",
        icon: <IconCheck size={18} />,
      });
    } catch {
      notifications.show({
        title: "Couldn't copy",
        message: "Try copying the URL from your browser bar.",
        color: "red",
      });
    }
  };

  return (
    <Menu
      position="bottom-end"
      shadow="md"
      radius="md"
      withArrow
      arrowPosition="center"
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="xl"
          aria-label={ariaLabel}
        >
          <IconDots size={24} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        {showCopy && (
          <Menu.Item
            leftSection={<IconLink size={16} />}
            onClick={handleCopy}
            style={{ color: "#F0F0F0" }}
          >
            {copyLabel}
          </Menu.Item>
        )}

        {showEdit && (
          <>
            {showCopy && <Menu.Divider color="rgba(255, 255, 255, 0.08)" />}
            <Menu.Label style={{ color: "#888" }}>Admin</Menu.Label>
            <Menu.Item
              leftSection={<IconEdit size={16} />}
              component="a"
              href={editUrl}
              style={{ color: "#F0F0F0" }}
            >
              Edit
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export default ContentActionsMenu;
